/**
 * QuestionnaireReview — review/edit screen for a custom questionnaire
 * template (Plan 2, Task 3).
 *
 * Opened from QuestionnaireShelf's "Review" action (needs_review row) or
 * "edit again" on a ready row. The parent hands this component the file_id
 * and the parsed instrument to start from (see index.tsx wiring note below);
 * this screen never fetches on its own besides the final saveInstrument PUT.
 *
 * Editable: name, timeframe, lang, items (add/remove/reorder/edit text), and
 * the single shared scale (add/remove steps: integer value + label). Save
 * validates client-side FIRST, then PUTs via useQuestionnaireFiles().
 * saveInstrument; Cancel discards all local edits.
 *
 * VALIDATION MIRROR (must stay in lockstep with the backend): every rule and
 * every numeric cap here is copied from
 * apps/infra/lambdas/questionnaires/files_common.py's validate_instrument —
 * unique int scale values, >=2 scale steps, non-empty item texts and name,
 * and the MAX_ITEMS/MAX_SCALE_STEPS/MAX_NAME_CHARS/MAX_TIMEFRAME_CHARS/
 * MAX_ITEM_TEXT_CHARS/MAX_SCALE_LABEL_CHARS caps. If the backend validator's
 * rules or caps ever change, update the constants and validateDraft() below
 * in the same change — otherwise a save that passes here could still be
 * rejected server-side (or worse, something the backend would reject slips
 * past a stale, too-lenient client check).
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui";
import { Drawer, DrawerHeader, DrawerBody, DrawerFooter } from "../../components/layout";
import { useQuestionnaireFiles } from "./useQuestionnaireFiles";
import type { CustomInstrument } from "./useQuestionnaireFiles";
import "./QuestionnaireReview.css";

// Mirrors files_common.py exactly — see the module docstring above.
const MAX_ITEMS = 200;
const MAX_SCALE_STEPS = 20;
const MAX_NAME_CHARS = 300;
const MAX_TIMEFRAME_CHARS = 300;
const MAX_ITEM_TEXT_CHARS = 2000;
const MAX_SCALE_LABEL_CHARS = 200;

type ItemDraft = { id: string; text: string };
type ScaleStepDraft = { localId: string; value: string; label: string };

export type QuestionnaireReviewProps = {
  fileId: string;
  instrument: CustomInstrument;
  onSaved: () => void;
  onCancel: () => void;
};

function uid(prefix: string): string {
  const rand = (globalThis.crypto as any)?.randomUUID?.() ?? Math.random().toString(36).slice(2, 10);
  return `${prefix}-${rand}`;
}

type ValidationResult = {
  errors: string[];
  itemErrors: Record<string, string>;
  scaleErrors: Record<string, string>;
};

function validateDraft(
  name: string,
  timeframe: string,
  items: ItemDraft[],
  scale: ScaleStepDraft[],
  t: (key: string, opts?: Record<string, unknown>) => string,
): ValidationResult {
  const errors: string[] = [];
  const itemErrors: Record<string, string> = {};
  const scaleErrors: Record<string, string> = {};

  const trimmedName = name.trim();
  if (!trimmedName) {
    errors.push(t("qreview.errorNameRequired"));
  } else if (trimmedName.length > MAX_NAME_CHARS) {
    errors.push(t("qreview.errorNameTooLong", { max: MAX_NAME_CHARS }));
  }

  const trimmedTimeframe = timeframe.trim();
  if (trimmedTimeframe.length > MAX_TIMEFRAME_CHARS) {
    errors.push(t("qreview.errorTimeframeTooLong", { max: MAX_TIMEFRAME_CHARS }));
  }

  if (items.length === 0) {
    errors.push(t("qreview.errorItemsRequired"));
  } else if (items.length > MAX_ITEMS) {
    errors.push(t("qreview.errorItemsTooMany", { count: items.length, max: MAX_ITEMS }));
  }

  const seenIds = new Set<string>();
  items.forEach((it) => {
    const text = it.text.trim();
    if (seenIds.has(it.id)) {
      itemErrors[it.id] = t("qreview.errorItemIdDuplicate");
    } else if (!text) {
      itemErrors[it.id] = t("qreview.errorItemTextRequired");
    } else if (text.length > MAX_ITEM_TEXT_CHARS) {
      itemErrors[it.id] = t("qreview.errorItemTextTooLong", { max: MAX_ITEM_TEXT_CHARS });
    }
    seenIds.add(it.id);
  });

  if (scale.length < 2) {
    errors.push(t("qreview.errorScaleStepsMin"));
  } else if (scale.length > MAX_SCALE_STEPS) {
    errors.push(t("qreview.errorScaleStepsTooMany", { count: scale.length, max: MAX_SCALE_STEPS }));
  }

  const valueCounts = new Map<number, number>();
  const parsedValues: Record<string, number | null> = {};
  scale.forEach((s) => {
    const trimmedVal = s.value.trim();
    const n = Number(trimmedVal);
    if (trimmedVal === "" || !Number.isInteger(n)) {
      parsedValues[s.localId] = null;
    } else {
      parsedValues[s.localId] = n;
      valueCounts.set(n, (valueCounts.get(n) ?? 0) + 1);
    }
  });
  scale.forEach((s) => {
    const v = parsedValues[s.localId];
    if (v === null) {
      scaleErrors[s.localId] = t("qreview.errorScaleValueInvalid");
      return;
    }
    if ((valueCounts.get(v) ?? 0) > 1) {
      scaleErrors[s.localId] = t("qreview.errorScaleValueDuplicate");
      return;
    }
    const label = s.label.trim();
    if (!label) {
      scaleErrors[s.localId] = t("qreview.errorScaleLabelRequired");
    } else if (label.length > MAX_SCALE_LABEL_CHARS) {
      scaleErrors[s.localId] = t("qreview.errorScaleLabelTooLong", { max: MAX_SCALE_LABEL_CHARS });
    }
  });

  return { errors, itemErrors, scaleErrors };
}

export const QuestionnaireReview: React.FC<QuestionnaireReviewProps> = ({
  fileId,
  instrument,
  onSaved,
  onCancel,
}) => {
  const { t } = useTranslation();
  const api = useQuestionnaireFiles();

  const [name, setName] = useState(instrument.name);
  const [timeframe, setTimeframe] = useState(instrument.timeframe ?? "");
  const [lang, setLang] = useState<"en" | "hu">(instrument.lang);
  const [items, setItems] = useState<ItemDraft[]>(() =>
    instrument.items.map((it) => ({ id: it.id, text: it.text })),
  );
  const [scale, setScale] = useState<ScaleStepDraft[]>(() =>
    instrument.scale.map((s) => ({ localId: uid("scale"), value: String(s.value), label: s.label })),
  );

  const [errors, setErrors] = useState<string[]>([]);
  const [itemErrors, setItemErrors] = useState<Record<string, string>>({});
  const [scaleErrors, setScaleErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const addItem = () => setItems((prev) => [...prev, { id: uid("item"), text: "" }]);
  const removeItem = (id: string) => setItems((prev) => prev.filter((it) => it.id !== id));
  const updateItemText = (id: string, text: string) =>
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, text } : it)));
  const moveItem = (id: string, dir: -1 | 1) =>
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.id === id);
      const newIdx = idx + dir;
      if (idx < 0 || newIdx < 0 || newIdx >= prev.length) return prev;
      const copy = [...prev];
      [copy[idx], copy[newIdx]] = [copy[newIdx], copy[idx]];
      return copy;
    });

  const addScaleStep = () => setScale((prev) => [...prev, { localId: uid("scale"), value: "", label: "" }]);
  const removeScaleStep = (localId: string) => setScale((prev) => prev.filter((s) => s.localId !== localId));
  const updateScaleValue = (localId: string, value: string) =>
    setScale((prev) => prev.map((s) => (s.localId === localId ? { ...s, value } : s)));
  const updateScaleLabel = (localId: string, label: string) =>
    setScale((prev) => prev.map((s) => (s.localId === localId ? { ...s, label } : s)));

  const handleSave = async () => {
    setSaveError(null);
    const result = validateDraft(name, timeframe, items, scale, t);
    setErrors(result.errors);
    setItemErrors(result.itemErrors);
    setScaleErrors(result.scaleErrors);

    const hasErrors =
      result.errors.length > 0 ||
      Object.keys(result.itemErrors).length > 0 ||
      Object.keys(result.scaleErrors).length > 0;
    if (hasErrors) return; // loud inline errors already set; no save attempt

    const payload: CustomInstrument = {
      name: name.trim(),
      lang,
      items: items.map((it) => ({ id: it.id, text: it.text.trim() })),
      scale: scale.map((s) => ({ value: Math.trunc(Number(s.value.trim())), label: s.label.trim() })),
    };
    const trimmedTimeframe = timeframe.trim();
    if (trimmedTimeframe) payload.timeframe = trimmedTimeframe;

    setSaving(true);
    try {
      await api.saveInstrument(fileId, payload);
      onSaved();
    } catch (e: any) {
      console.error("[QuestionnaireReview] saveInstrument:", e);
      setSaveError(e?.message ? String(e.message) : t("qreview.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer open onClose={onCancel}>
      <DrawerHeader title={t("qreview.title")} onClose={onCancel} />
      <DrawerBody>
        {saveError || errors.length ? (
          <div role="alert" className="qr-errors">
            {saveError ? <div>{saveError}</div> : null}
            {errors.map((e, i) => (
              <div key={i}>{e}</div>
            ))}
          </div>
        ) : null}

        <div className="qr-field">
          <label className="qr-label" htmlFor="qr-name">
            {t("qreview.nameLabel")}
          </label>
          <input
            id="qr-name"
            className="qr-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
          />
        </div>

        <div className="qr-field">
          <label className="qr-label" htmlFor="qr-timeframe">
            {t("qreview.timeframeLabel")}
          </label>
          <input
            id="qr-timeframe"
            className="qr-input"
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            placeholder={t("qreview.timeframePlaceholder")}
            disabled={saving}
          />
        </div>

        <div className="qr-field">
          <label className="qr-label" htmlFor="qr-lang">
            {t("qreview.langLabel")}
          </label>
          <select
            id="qr-lang"
            className="qr-input"
            value={lang}
            onChange={(e) => setLang(e.target.value === "hu" ? "hu" : "en")}
            disabled={saving}
          >
            <option value="en">{t("qreview.langEn")}</option>
            <option value="hu">{t("qreview.langHu")}</option>
          </select>
        </div>

        <div className="qr-section">
          <div className="qr-sectionHead">
            <h4 className="qr-sectionTitle">{t("qreview.itemsTitle")}</h4>
            <Button size="sm" onClick={addItem} disabled={saving}>
              {t("qreview.addItem")}
            </Button>
          </div>
          <div className="qr-list">
            {items.map((it, idx) => (
              <div key={it.id} className="qr-itemRow">
                <div className="qr-itemMain">
                  <span className="qr-itemIndex">{idx + 1}.</span>
                  <input
                    className="qr-input"
                    value={it.text}
                    onChange={(e) => updateItemText(it.id, e.target.value)}
                    placeholder={t("qreview.itemTextPlaceholder")}
                    disabled={saving}
                  />
                  <div className="qr-itemActions">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => moveItem(it.id, -1)}
                      disabled={saving || idx === 0}
                      aria-label={t("qreview.moveUp")}
                    >
                      ↑
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => moveItem(it.id, 1)}
                      disabled={saving || idx === items.length - 1}
                      aria-label={t("qreview.moveDown")}
                    >
                      ↓
                    </Button>
                    <Button size="sm" variant="danger" onClick={() => removeItem(it.id)} disabled={saving}>
                      {t("common.delete")}
                    </Button>
                  </div>
                </div>
                {itemErrors[it.id] ? <div className="qr-rowError">{itemErrors[it.id]}</div> : null}
              </div>
            ))}
          </div>
        </div>

        <div className="qr-section">
          <div className="qr-sectionHead">
            <h4 className="qr-sectionTitle">{t("qreview.scaleTitle")}</h4>
            <Button size="sm" onClick={addScaleStep} disabled={saving}>
              {t("qreview.addScaleStep")}
            </Button>
          </div>
          <div className="qr-list">
            {scale.map((s) => (
              <div key={s.localId} className="qr-scaleRow">
                <div className="qr-scaleMain">
                  <input
                    className="qr-inputSmall"
                    value={s.value}
                    onChange={(e) => updateScaleValue(s.localId, e.target.value)}
                    placeholder={t("qreview.scaleValuePlaceholder")}
                    inputMode="numeric"
                    disabled={saving}
                  />
                  <input
                    className="qr-input"
                    value={s.label}
                    onChange={(e) => updateScaleLabel(s.localId, e.target.value)}
                    placeholder={t("qreview.scaleLabelPlaceholder")}
                    disabled={saving}
                  />
                  <Button size="sm" variant="danger" onClick={() => removeScaleStep(s.localId)} disabled={saving}>
                    {t("common.delete")}
                  </Button>
                </div>
                {scaleErrors[s.localId] ? <div className="qr-rowError">{scaleErrors[s.localId]}</div> : null}
              </div>
            ))}
          </div>
        </div>
      </DrawerBody>
      <DrawerFooter>
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          {t("common.cancel")}
        </Button>
        <Button variant="primary" onClick={() => void handleSave()} disabled={saving}>
          {saving ? t("qreview.saving") : t("common.save")}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
};

export default QuestionnaireReview;
